/**
 * Cohort Enrollment Page
 * Public page for self-registration via invite code
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCohortInvite, useEnrollInCohort } from "@hooks/useCohorts";
import { Button, Card } from "@components/common";
import { formatDate } from "@utils/format";

export function EnrollPage() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const [email, setEmail] = useState("");
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolledCohortName, setEnrolledCohortName] = useState("");

  const {
    data: cohortInfo,
    isLoading,
    error,
  } = useCohortInvite(inviteCode);

  const enrollMutation = useEnrollInCohort();

  const handleEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode || !email) return;

    try {
      const result = await enrollMutation.mutateAsync({
        inviteCode,
        email,
      });
      setIsEnrolled(true);
      setEnrolledCohortName(result.cohortName);
    } catch (error) {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Error state (invalid invite code)
  if (error || !cohortInfo) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="mb-4">
            <svg
              className="w-16 h-16 mx-auto text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invalid Invite Link
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This invite link is invalid or has expired. Please contact the
            workshop organizer for a new link.
          </p>
          <Link to="/">
            <Button variant="outline">Return Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Cohort is full
  if (cohortInfo.isFull) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="mb-4">
            <svg
              className="w-16 h-16 mx-auto text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Cohort is Full
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Unfortunately, this cohort has reached its maximum capacity. Please
            contact the organizer for alternative options.
          </p>
          <Link to="/">
            <Button variant="outline">Return Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Success state
  if (isEnrolled) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="mb-4">
            <svg
              className="w-16 h-16 mx-auto text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Successfully Enrolled!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You have been enrolled in <strong>{enrolledCohortName}</strong>.
            You'll receive an email with further instructions and access to
            Agent Designer.
          </p>
          <div className="space-y-3">
            <Link to="/login">
              <Button className="w-full">Sign In to Agent Hub</Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Return Home
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Enrollment form
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Join Workshop
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Enter your email to enroll in this cohort
          </p>
        </div>

        {/* Cohort Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-2">
            {cohortInfo.cohortName}
          </h2>
          {cohortInfo.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {cohortInfo.description}
            </p>
          )}
          <div className="space-y-1 text-sm">
            {cohortInfo.workshopName && (
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                Workshop: {cohortInfo.workshopName}
              </div>
            )}
            {cohortInfo.startDate && (
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Starts: {formatDate(cohortInfo.startDate)}
              </div>
            )}
            {cohortInfo.endDate && (
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Ends: {formatDate(cohortInfo.endDate)}
              </div>
            )}
          </div>
        </div>

        {/* Enrollment Form */}
        <form onSubmit={handleEnroll} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          {enrollMutation.error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {(enrollMutation.error as { detail?: string })?.detail ||
                "Failed to enroll. Please try again."}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            isLoading={enrollMutation.isPending}
          >
            Enroll Now
          </Button>
        </form>

        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          By enrolling, you agree to receive emails about this workshop and gain
          access to Google Cloud Agent Designer.
        </p>
      </Card>
    </div>
  );
}
